import React from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from "@mui/material";
import { PRIMARY_SILVER_COLOR } from "..";
import { TextField } from "ui-components-library";

export interface SelectOption<V extends string | number = number> {
  value: V;
  label: string;
}

type Props<V extends string | number = string | number> = Omit<
  SelectProps,
  "label" | "onChange" | "value"
> & {
  label?: React.ReactNode;
  options: SelectOption<V>[];
  wrapperStyles?: React.CSSProperties;
  labelStyles?: React.CSSProperties;
  helperText?: string;
  onChange?: (value: V) => void;
  value?: V;
  loading?: boolean;
  placeholder?: string;
};

function CustomSelectComponent<V extends string | number = string | number>(
  {
    label,
    id,
    options,
    wrapperStyles,
    labelStyles,
    helperText,
    error,
    onChange,
    size = "small",
    value,
    loading = false,
    placeholder = "Select From List",
    ...rest
  }: Props<V>,
  ref: React.Ref<HTMLSelectElement>,
) {
  const inputId = id ?? rest?.name;

  return (
    <Box sx={wrapperStyles}>
      {label && (
        <Typography
          component="label"
          variant="caption"
          htmlFor={inputId}
          sx={{
            fontSize: "13px", // to match legacy design
            color: PRIMARY_SILVER_COLOR[500],
            ...labelStyles,
          }}
        >
          {label}
        </Typography>
      )}

      <FormControl fullWidth error={error}>
        <Select
          id={inputId}
          variant="outlined"
          ref={ref}
          sx={{
            borderRadius: 0,
            fontSize: "20px",
            "& .MuiSelect-select": {
              paddingTop: "6px",
              paddingBottom: "6px",
            },
            ...rest.sx,
          }}
          onChange={(event) => {
            onChange?.(event.target.value as V);
          }}
          value={value}
          size={size}
          disabled={loading}
          displayEmpty={!!placeholder}
          renderValue={(selected): React.ReactNode => {
            if (loading) {
              return (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  <span style={{ fontSize: "20px" }}>Loading...</span>
                </Box>
              );
            }

            if (!selected && placeholder) {
              return (
                <span
                  style={{
                    color: PRIMARY_SILVER_COLOR[600],
                    fontSize: "20px",
                  }}
                >
                  {placeholder}
                </span>
              );
            }

            const selectedOption = options.find(
              (opt) => opt.value === selected,
            );

            return selectedOption ? selectedOption.label : String(selected);
          }}
          {...rest}
        >
          {loading ? (
            <MenuItem disabled>
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <span>Loading...</span>
              </Box>
            </MenuItem>
          ) : (
            options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))
          )}
        </Select>
        {helperText && <TextField children={helperText} />}
      </FormControl>
    </Box>
  );
}

export const CustomSelect = React.forwardRef(CustomSelectComponent) as <
  V extends string | number = string | number,
>(
  props: Props<V> & { ref?: React.Ref<HTMLSelectElement> },
) => React.ReactElement;

(CustomSelect as React.FC).displayName = "CustomSelect";
